import { useState, useEffect } from "react";
import firebaseService from "../firebase_service/firebaseService";
import MsTable from "../components/Widgets/Table";
import ButtonLoader from "../components/Widgets/ButtonLoader";
import { Version, colType } from "../Types";
import { NavLink } from "react-router-dom";
import adminApiClient, { VersionUpdate } from "../api/admin";
import DSCheckbox from "./system-design/DSCheckBox";
import DSTypography from "./system-design/DSTypography";
import DSBox from "./system-design/DSBox";
import DSTextField from "./system-design/DSTextField";
import DSDialog from "./system-design/DSDialog";
import DSDialogTitle from "./system-design/DSDialogTitle";
import DSDialogContent from "./system-design/DSDialogContent";
import DSDialogActions from "./system-design/DSDialogActions";
import DSSecondaryButton from "./system-design/DSSecondaryButton";
import DSCircularProgress from "./system-design/DSCircularProgress";
import DSTooltip from "./system-design/DSTooltip";
import DSAccordion from "./system-design/DSAccordion";
import DSAccordionSummary from "./system-design/DSAccordionSummary";
import DSAccordionDetails from "./system-design/DSAccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSearchParams } from "react-router-dom";
import { FaPencilAlt } from "react-icons/fa";
import { GoGitBranch } from "react-icons/go";
import PageHeader from "../components/PageHeader";

const FORM_VERSIONS: Array<{ key: VersionUpdate["update"]; label: string }> = [
  { key: "public", label: "Public version" },
  { key: "beta", label: "Beta version" },
  { key: "artistVersion", label: "Artist version" },
  { key: "obsolete", label: "Obsolete version" },
  { key: "publicOldPlayer", label: "Migration version" },
];

type VersionState = {
  value: string;
  error: string;
};

type VersionStates = Record<string, VersionState>;

// The published value currently stored on the config document, as a string so
// it can be compared against the (string) form state. Keys that were never
// published yet - e.g. a freshly added one - resolve to an empty string.
const publishedValue = (key: string): string => {
  const value = firebaseService.general?.[key];
  return value === undefined || value === null ? "" : value.toString();
};

// GitHub-style branch label for versions built from a branch other than main.
const BranchLabel = ({ branch }: { branch?: string | null }) => (
  <DSTooltip title={branch || "Branch name unknown"}>
    <DSBox
      sx={(theme) => ({
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        minWidth: 0,
        maxWidth: "100%",
        px: "6px",
        py: "1px",
        borderRadius: "6px",
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
        fontSize: "12px",
        lineHeight: "20px",
        color: theme.palette.mode === "dark" ? "#4493f8" : "#0969da",
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(56, 139, 253, 0.15)"
            : "#ddf4ff",
      })}
    >
      <GoGitBranch style={{ flexShrink: 0 }} />
      <DSBox
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {branch || "unknown"}
      </DSBox>
    </DSBox>
  </DSTooltip>
);

function Versions() {
  const [data, setData] = useState<Array<Version>>();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [versionKeyToSubmit, setVersionKeyToSubmit] = useState<string | null>(
    null,
  );
  const [hideVersionsWithoutClocks, setHideVersionsWithoutClocks] =
    useState(false);

  const [searchParams] = useSearchParams();

  const [versionStates, setVersionStates] = useState<VersionStates>(
    FORM_VERSIONS.reduce<VersionStates>((accumulator, { key }) => {
      accumulator[key] = { value: "", error: "" };
      return accumulator;
    }, {}),
  );

  useEffect(() => {
    const listener = (versions: Array<Version>) => setData([...versions]);
    firebaseService.listen<Version>("version", listener);
    return () => {
      firebaseService.removeListener("version", listener);
    };
  }, []);

  useEffect(() => {
    setVersionStates((prev) =>
      FORM_VERSIONS.reduce<VersionStates>((accumulator, { key }) => {
        accumulator[key] = {
          ...prev[key],
          value: publishedValue(key),
        };
        return accumulator;
      }, {}),
    );
  }, [firebaseService.general]);

  const handleInputChange = (key: string, value: string) => {
    const validated = data?.some((v) => v.version.toString() === value);

    setVersionStates((prev) => ({
      ...prev,
      [key]: {
        value,
        error: validated ? "" : "There is no such version",
      },
    }));
  };

  const handleCancel = () => {
    setShow(false);
    if (versionKeyToSubmit) {
      setVersionStates((prev) => ({
        ...prev,
        [versionKeyToSubmit]: {
          value: publishedValue(versionKeyToSubmit),
          error: "",
        },
      }));
    }
    setVersionKeyToSubmit(null);
  };

  const publishVersion = async (key: string) => {
    const version = versionStates[key].value;

    await adminApiClient.publishVersion({
      version: parseFloat(version),
      update: key as VersionUpdate["update"],
    });

    setVersionStates((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: version,
      },
    }));
  };

  const handleSubmit = async (key: string) => {
    setVersionKeyToSubmit(key);
    setShow(true);
  };

  const handleDeprecatedToggle = async (
    version: Version,
    isDeprecated: boolean,
  ) => {
    try {
      setData((prevData) => {
        if (!prevData) return prevData;
        return prevData.map((v) =>
          v.id === version.id ? { ...v, deprecated: isDeprecated } : v,
        );
      });

      await firebaseService.db
        .collection("version")
        .doc(version.id)
        .update({ deprecated: isDeprecated });
      firebaseService.updateCachedItem("version", version.id, {
        deprecated: isDeprecated,
      });
    } catch (error) {
      console.error("Error updating deprecated status:", error);
      setData((prevData) => {
        if (!prevData) return prevData;
        return prevData.map((v) =>
          v.id === version.id ? { ...v, deprecated: !isDeprecated } : v,
        );
      });
    }
  };

  const handleIsFeatureToggle = async (
    version: Version,
    isFeature: boolean,
  ) => {
    try {
      setData((prevData) => {
        if (!prevData) return prevData;
        return prevData.map((v) =>
          v.id === version.id ? { ...v, isFeature } : v,
        );
      });

      await firebaseService.db
        .collection("version")
        .doc(version.id)
        .update({ isFeature });
      firebaseService.updateCachedItem("version", version.id, { isFeature });
    } catch (error) {
      console.error("Error updating isFeature status:", error);
      setData((prevData) => {
        if (!prevData) return prevData;
        return prevData.map((v) =>
          v.id === version.id ? { ...v, isFeature: !isFeature } : v,
        );
      });
    }
  };

  const handleBranchBuildToggle = async (
    version: Version,
    branchBuild: boolean,
  ) => {
    try {
      setData((prevData) => {
        if (!prevData) return prevData;
        return prevData.map((v) =>
          v.id === version.id ? { ...v, branchBuild } : v,
        );
      });

      await firebaseService.db
        .collection("version")
        .doc(version.id)
        .update({ branchBuild });
      firebaseService.updateCachedItem("version", version.id, { branchBuild });
    } catch (error) {
      console.error("Error updating branchBuild status:", error);
      setData((prevData) => {
        if (!prevData) return prevData;
        return prevData.map((v) =>
          v.id === version.id ? { ...v, branchBuild: !branchBuild } : v,
        );
      });
    }
  };

  const [editingDescriptionId, setEditingDescriptionId] = useState<
    string | null
  >(null);
  const [editingDescriptionValue, setEditingDescriptionValue] = useState("");

  const handleDescriptionSave = async (version: Version) => {
    const newDescription = editingDescriptionValue;
    setEditingDescriptionId(null);
    try {
      setData((prevData) => {
        if (!prevData) return prevData;
        return prevData.map((v) =>
          v.id === version.id ? { ...v, description: newDescription } : v,
        );
      });

      await firebaseService.db
        .collection("version")
        .doc(version.id)
        .update({ description: newDescription });
      firebaseService.updateCachedItem("version", version.id, {
        description: newDescription,
      });
    } catch (error) {
      console.error("Error updating description:", error);
      setData((prevData) => {
        if (!prevData) return prevData;
        return prevData.map((v) =>
          v.id === version.id ? { ...v, description: version.description } : v,
        );
      });
    }
  };

  const tableColumns: Array<colType> = [
    { sort: true, size: 170, label: "Version", field: "version" },
    { sort: true, label: "Date", field: "date" },
    { size: 700, sort: false, label: "Description", field: "description" },
    { sort: true, label: "Features", field: "isFeature" },
    { sort: true, label: "Branch build", field: "branchBuild" },
    { sort: true, label: "Was Beta", field: "wasBeta" },
    { sort: true, label: "Was Public", field: "wasPublic" },
    { sort: true, label: "Was Artist", field: "wasArtist" },
    { sort: true, label: "Deprecated", field: "deprecated" },
    { sort: true, label: "Devices", field: "devices" },
  ];

  const tableRows = (r: Version, key: string) => {
    // Treat negative devices as 0
    const normalizedDevices = Math.max(0, r.devices || 0);
    const isDisabled = normalizedDevices === 0;

    return {
      version: (
        <DSBox
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
            minWidth: 0,
            width: "100%",
            px: 1,
          }}
        >
          <p className={isDisabled ? "disabled" : ""} style={{ margin: 0 }}>
            {r.version}
          </p>
          {r.branchBuild && <BranchLabel branch={r.branch} />}
        </DSBox>
      ),
      date: (
        <p className={isDisabled ? "disabled" : ""}>
          {r.date.toLocaleString("en-GB")}
        </p>
      ),
      description:
        editingDescriptionId === r.id ? (
          <DSBox onClick={(e) => e.stopPropagation()} sx={{ width: "100%" }}>
            <DSTextField
              multiline
              rows={2}
              fullWidth
              size="small"
              value={editingDescriptionValue}
              onChange={(e) => setEditingDescriptionValue(e.target.value)}
              onBlur={() => handleDescriptionSave(r)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleDescriptionSave(r);
                }
                if (e.key === "Escape") {
                  setEditingDescriptionId(null);
                }
              }}
              inputRef={(el: HTMLTextAreaElement | null) => {
                if (el) {
                  el.focus();
                  el.selectionStart = el.value.length;
                  el.selectionEnd = el.value.length;
                }
              }}
            />
          </DSBox>
        ) : (
          <DSBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              width: "100%",
            }}
          >
            <p
              className={isDisabled ? "disabled" : ""}
              style={{ flex: 1, margin: 0 }}
            >
              {r.description}
            </p>
            <FaPencilAlt
              style={{
                cursor: "pointer",
                flexShrink: 0,
                color: "#888",
                fontSize: "12px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setEditingDescriptionId(r.id);
                setEditingDescriptionValue(r.description);
              }}
            />
          </DSBox>
        ),
      isFeature: (
        <DSBox
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DSCheckbox
            checked={r.isFeature || false}
            onChange={(e) => handleIsFeatureToggle(r, e.target.checked)}
          />
        </DSBox>
      ),
      branchBuild: (
        <DSBox
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DSCheckbox
            checked={r.branchBuild || false}
            onChange={(e) => handleBranchBuildToggle(r, e.target.checked)}
          />
        </DSBox>
      ),
      wasPublic: <p>{r.wasPublic?.toString()}</p>,
      wasBeta: <p>{r.wasBeta?.toString()}</p>,
      wasArtist: <p>{r.wasArtist?.toString()}</p>,
      deprecated: (
        <DSBox
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <DSCheckbox
            checked={r.deprecated || false}
            onChange={(e) => handleDeprecatedToggle(r, e.target.checked)}
          />
        </DSBox>
      ),
      devices: (
        <NavLink
          style={isDisabled ? { cursor: "not-allowed" } : {}}
          to={
            normalizedDevices > 0
              ? `/admin/devices?fromVersion=${r.version}&toVersion=${r.version}`
              : ""
          }
        >
          <p className={isDisabled ? "disabled" : ""}>{normalizedDevices}</p>
        </NavLink>
      ),
      key: key,
    };
  };

  const renderForms = () => {
    return (
      <DSBox>
        <DSBox
          sx={{
            mb: "15px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <DSCheckbox
            id="hide-versions-without-clocks"
            title="Hide versions without clocks"
            name="Hide versions without clocks"
            checked={hideVersionsWithoutClocks}
            onChange={(e) => setHideVersionsWithoutClocks(e.target.checked)}
          />
          <DSTypography style={{ marginRight: "8px" }}>
            {`Hide versions without clocks`}
          </DSTypography>
        </DSBox>
        {FORM_VERSIONS.map(({ key, label }) => (
          <DSBox sx={{ display: "inline-block", mr: 2 }} key={key}>
            <form noValidate={!versionStates[key].error}>
              <DSTypography sx={{ fontWeight: "bold" }}>{label}:</DSTypography>
              <DSBox sx={{ mb: 1 }}>
                <DSTextField
                  id={`form-${key}`}
                  size="small"
                  value={versionStates[key].value || ""}
                  error={versionStates[key].error !== ""}
                  helperText={versionStates[key].error}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  type="number"
                  placeholder={`Enter ${label}`}
                  required
                />
              </DSBox>
              <DSBox>
                {versionStates[key].value !== "" &&
                  versionStates[key].value !== publishedValue(key) &&
                  !versionStates[key].error && (
                    <ButtonLoader
                      title={`Send to ${label}`}
                      onClick={() => handleSubmit(key)}
                    />
                  )}
              </DSBox>
            </form>
          </DSBox>
        ))}
      </DSBox>
    );
  };

  // Filter data based on hideVersionsWithoutClocks checkbox
  const filteredData = hideVersionsWithoutClocks
    ? data?.filter((version) => Math.max(0, version.devices || 0) > 0)
    : data;

  console.log(searchParams.get("version"));

  return (
    <>
      <PageHeader
        overline="Devices"
        title="Versions"
        subtitle="Firmware and app rollout — publish public, beta, obsolete and migration versions and track adoption"
      />
      <DSAccordion
        disableGutters
        defaultExpanded={false}
        sx={{
          mb: 2,
          bgcolor: "surface.panel",
          border: "1px solid",
          borderColor: "surface.border",
          borderRadius: "16px !important",
          boxShadow: "none",
          backgroundImage: "none",
          "&:before": { display: "none" },
        }}
      >
        <DSAccordionSummary expandIcon={<ExpandMoreIcon />}>
          <DSBox
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              columnGap: 2,
              rowGap: 0.5,
            }}
          >
            <DSTypography variant="h6">Publish versions</DSTypography>
            <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
              {FORM_VERSIONS.filter(({ key }) => firebaseService.general?.[key])
                .map(
                  ({ key, label }) =>
                    `${label}: ${firebaseService.general?.[key]}`,
                )
                .join("  •  ")}
            </DSTypography>
          </DSBox>
        </DSAccordionSummary>
        <DSAccordionDetails>{renderForms()}</DSAccordionDetails>
      </DSAccordion>
      {data ? (
        <MsTable
          id={"versions"}
          rowHeight={72}
          rowBuild={tableRows}
          search={(row: Version, val: string) => {
            // Branch names can start with digits, so match them before the
            // version-number shortcut.
            if (row.branch?.toLowerCase().includes(val)) return true;
            if (parseInt(val)) return row.version === parseInt(val);
            return row.description.toLowerCase().includes(val);
          }}
          data={filteredData!}
          initSort={"version"}
          cols={tableColumns}
          param={searchParams.get("version") || undefined}
          title="Versions"
        />
      ) : (
        <DSCircularProgress />
      )}
      <DSDialog
        open={show}
        onClose={() => {
          if (!loading) setShow(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DSDialogTitle>Are you sure?</DSDialogTitle>
        <DSDialogContent>
          {versionKeyToSubmit === "public"
            ? "This version will be available for all users."
            : versionKeyToSubmit === "beta"
              ? "This version will be sent to beta testers."
              : versionKeyToSubmit === "artistVersion"
                ? "This version will be sent to artist clocks (they stay on the public version if it is newer)."
                : versionKeyToSubmit === "obsolete"
                  ? "This version will be marked as obsolete."
                  : versionKeyToSubmit === "publicOldPlayer"
                    ? "This version will be set as the migration version for old players."
                    : ""}
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={() => handleCancel()}>
            Cancel
          </DSSecondaryButton>
          <ButtonLoader
            title="OK"
            onClick={async () => {
              setLoading(true);
              await publishVersion(versionKeyToSubmit!).then(() => {
                setLoading(false);
                setShow(false);
              });
            }}
          />
        </DSDialogActions>
      </DSDialog>
    </>
  );
}

export default Versions;
